// MobileActionSheet — the bottom sheet behind a long-press on the phone.
//
// A portal-rendered modal sheet that slides up from the bottom edge,
// carrying a short list of actions. Mirrors ConfirmDialog's APG plumbing
// (scrim, focus park + trap, Escape, restore-to-opener) so the two
// overlays behave identically; the visual shape differs (bottom-anchored
// sheet rather than centred card).

import { Check } from "lucide-react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { usePortalRoot } from "~/components/EmbedPortalContext";
import { isEmbeddedPortal } from "~/lib/portal-positioning";
import { mobileStrings } from "~/components/mobile/strings";
import { ICON_STROKE } from "~/lib/icon";

export interface MobileSheetAction {
  key: string;
  label: string;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
  /** The current choice in a pick-one sheet: shows a check. */
  selected?: boolean;
}

export interface MobileActionSheetProps {
  /** Accessible name for the sheet (e.g. "Actions for Design"). */
  label: string;
  actions: MobileSheetAction[];
  onClose: () => void;
}

export function MobileActionSheet({ label, actions, onClose }: MobileActionSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const portalRoot = usePortalRoot();

  useEffect(() => {
    previouslyFocusedRef.current =
      typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null;
    const first = sheetRef.current?.querySelector<HTMLButtonElement>("button:not([disabled])");
    first?.focus();
    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = Array.from(
      sheetRef.current?.querySelectorAll<HTMLButtonElement>("button:not([disabled])") ?? [],
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!portalRoot) return null;

  const scrimPosition: "fixed" | "absolute" = isEmbeddedPortal(portalRoot) ? "absolute" : "fixed";

  const overlay = (
    <div
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={handleKeyDown}
      style={{
        position: scrimPosition,
        inset: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        background: "var(--chat-scrim-dialog)",
        zIndex: 900,
      }}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="chat-safe-bottom chat-safe-left chat-safe-right"
        style={{
          display: "flex",
          flexDirection: "column",
          background: "var(--chat-surface-dialog)",
          borderTopLeftRadius: "var(--chat-radius-xl)",
          borderTopRightRadius: "var(--chat-radius-xl)",
          boxShadow: "var(--chat-shadow-dialog)",
          padding: "var(--chat-space-2) 0",
        }}
      >
        {actions.map((action) => (
          <button
            key={action.key}
            type="button"
            disabled={action.disabled}
            aria-pressed={action.selected}
            onClick={() => {
              action.onSelect();
              onClose();
            }}
            className="chat-button outline-none focus-visible:shadow-neon-cyan"
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              height: 52,
              padding: "0 var(--chat-space-5)",
              border: "none",
              background: "transparent",
              color: action.destructive
                ? "var(--chat-state-danger)"
                : action.disabled
                  ? "var(--chat-fg-muted)"
                  : "var(--chat-fg-primary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              fontWeight: 300,
              textAlign: "left",
              cursor: action.disabled ? "default" : "pointer",
            }}
          >
            <span style={{ flex: 1 }}>{action.label}</span>
            {action.selected && <Check aria-hidden size={16} strokeWidth={ICON_STROKE} />}
          </button>
        ))}
        <button
          type="button"
          onClick={onClose}
          className="chat-button outline-none focus-visible:shadow-neon-cyan"
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: 52,
            marginTop: "var(--chat-space-1)",
            padding: "0 var(--chat-space-5)",
            border: "none",
            borderTop: "1px solid var(--chat-surface-border)",
            background: "transparent",
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            fontWeight: 300,
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          {mobileStrings.sheet.close}
        </button>
      </div>
    </div>
  );

  return createPortal(overlay, portalRoot);
}
